/**
 * Shrink a photo in the browser before upload (P24, 2026-10-10).
 *
 * A person photo only needs to look right in the page header, so it is scaled to at
 * most `maxSide` pixels on its longer side and re-encoded as JPEG — typically under
 * 100 KB instead of a several-MB phone picture. Transparent PNG areas become white.
 * Everything happens locally (canvas); nothing leaves the browser until Save.
 */
export async function shrinkImage(file: File, maxSide = 512, quality = 0.85): Promise<File> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser cannot resize images.');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) throw new Error('The image could not be read.');
    const base = file.name.replace(/\.(png|jpe?g)$/i, '') || 'photo';
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
  } finally {
    bitmap.close();
  }
}
